import {
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
} from "@mui/material";

import { Alert } from "@mui/material";
import { useEffect, useRef, useState, useMemo, useContext } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  setReservationPaymentStatus,
  cancelReservation,
} from "../graphql/mutations";
import * as Sentry from "@sentry/react";
import { captureError } from "../utilities";
import { API, graphqlOperation } from "aws-amplify";
import { authPayment } from "../graphql/client-queries";
import { useFormik } from "formik";
import PaymentMethods from "../components/PaymentMethods";

import { AppContext } from "../contextProviders/AppContext";
import { gtmPurchase } from "../gtm";
import { getReservationInfo } from "../graphql/queries";

function P({ children }) {
  return (
    <Typography style={{ margin: "1em 0px" }} variant="body1">
      {children}
    </Typography>
  );
}
function P2({ children }) {
  return (
    <Typography style={{ margin: "1em 0px" }} variant="body2" component="p">
      {children}
    </Typography>
  );
}

function PaymentFailure({
  reservationId,
  group,
  navigate,
  setSnackbarMessage,
  setEmailConfirmationSuccess,
  setFailedOrderId,
  paymentOnlineOnly,
  theme,
  showReturnToCompanyUrl,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [payNow, setPayNow] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const { showBackToStartBtn } = useContext(AppContext);

  const INITIAL_FORM_DATA = {
    paymentMethod: "",
    paymentMode: "",
  };
  const validate = (values) => {
    const errors = {};
    return errors;
  };

  const FEATURE_PAYMENT_ONLINE_ONLY = useMemo(() => {
    return paymentOnlineOnly;
  }, [paymentOnlineOnly]);

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setPayNow(false); // Close the dialog
      const paymentMethod = paymentMethods.find((method) => {
        return method.name === values.paymentMethod;
      });
      const response = await API.graphql(
        graphqlOperation(authPayment, {
          reservationId: reservationId,
          paymentMethod: paymentMethod,
          origin: window.location.origin + window.location.pathname,
          originPath: "vahvistus",
        })
      );
      window.location.href =
        "https://www.vismapay.com/pbwapi/token/" +
        response.data.authPayment.token;
    } catch (e) {
      console.log(e);
      captureError("Auth payment again", "AUTH_PAYMENT_FAILED", e);
      setSubmitting(false);
      setSnackbarMessage("Jokin meni vikaan");
    }
  };

  const formik = useFormik({
    initialValues: INITIAL_FORM_DATA,
    validate,
    onSubmit,
  });

  const setPaymentOnPickup = async () => {
    try {
      setSubmitting(true);
      const result = await API.graphql(
        graphqlOperation(setReservationPaymentStatus, {
          reservationId: reservationId,
        })
      );
      if (!result.data.setReservationPaymentStatus.emailConfirmationSuccess) {
        Sentry.captureMessage("Send confirmation email failed", {
          tags: {
            code: "CONFIRMATION_EMAIL_FAILED",
            destinationEmail: result.destinationEmail,
          },
        });
      }
      setEmailConfirmationSuccess(
        result.data.setReservationPaymentStatus.emailConfirmationSuccess
      );
      setFailedOrderId(false);
      setSubmitting(false);
    } catch (e) {
      captureError("Set payment on pickup", "SET_PAYMENT_ON_PICKUP", e);
      setSubmitting(false);
      setSnackbarMessage("Jokin meni vikaan");
    }
  };

  const doCancelReservation = async () => {
    try {
      setSubmitting(true);
      const response = await API.graphql(
        graphqlOperation(cancelReservation, {
          reservationId: reservationId,
        })
      );
      const success = response.data.cancelReservation;
      if (success) {
        navigate("/");
      } else {
        captureError(
          "Canceling reservation success not true",
          "CANCEL_RESERVATION_FAILED",
          ""
        );
        setSubmitting(false);
        setSnackbarMessage("Jokin meni vikaan");
      }
      setSubmitting(false);
    } catch (e) {
      captureError("Canceling reservation", "CANCEL_RESERVATION_FAILED", e);
      setSubmitting(false);
      setSnackbarMessage("Jokin meni vikaan");
    }
  };

  return (
    <Paper
      sx={{
        maxWidth: 400,
        padding: `${theme.spacing(6)} ${theme.spacing(4)}`,
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(4),
        textAlign: "center",
      }}
    >
      <Typography variant="h4" component="h1">
        Maksu epäonnistui
      </Typography>

      <P>Maksaminen epäonnistui eikä varausta voitu vahvistaa. </P>
      <P>
        {FEATURE_PAYMENT_ONLINE_ONLY
          ? "Vahvistaaksei varauksen yritä maksua uudelleen"
          : "Vahvistaaksesi varauksen voit valita maksa noudettaessa, tai yrittää maksua uudelleen"}
      </P>
      <div
        style={{
          marginBottom: 20,
          "& > *": {
            margin: theme.spacing(1),
          },
          "& button": {
            width: "45%",
          },
          "& .singleBtn": {
            width: "100%",
            margin: 0,
          },
        }}
      >
        <Button
          onClick={() => setPayNow(true)}
          color="primary"
          size="medium"
          variant="contained"
          className={FEATURE_PAYMENT_ONLINE_ONLY ? "singleBtn" : ""}
          fullWidth={FEATURE_PAYMENT_ONLINE_ONLY}
          disabled={submitting}
        >
          Yritä maksua uudelleen
        </Button>
        {!FEATURE_PAYMENT_ONLINE_ONLY && (
          <Button
            onClick={() => setPaymentOnPickup()}
            color="primary"
            size="medium"
            variant="contained"
            disabled={submitting || FEATURE_PAYMENT_ONLINE_ONLY}
          >
            Maksa noudettaessa
          </Button>
        )}
      </div>
      {showBackToStartBtn && (
        <Button
          onClick={() => doCancelReservation()}
          color="primary"
          size="medium"
          variant="contained"
          disabled={submitting}
          fullWidth
        >
          Peruuta varaus
        </Button>
      )}
      {showReturnToCompanyUrl && (
        <Button
          onClick={() => {
            if (group === "tampereensaunalautta") {
              window.location.replace("https://saunalauttaristeilyt.fi/");
            }
          }}
          color="primary"
          size="medium"
          variant="contained"
          disabled={submitting}
          fullWidth
        >
          Palaa vuokraamon verkkosivuille
        </Button>
      )}
      {payNow && (
        <Dialog open={true} onClose={() => setPayNow(false)}>
          <DialogTitle>Valitse maksutapa (Visma Pay)</DialogTitle>
          <DialogContent>
            <PaymentMethods
              formik={formik}
              paymentMode="PENDING"
              paymentMethods={paymentMethods}
              setPaymentMethods={setPaymentMethods}
              confirmationView={true}
            />
          </DialogContent>
          <DialogActions>
            <Button color="primary" onClick={() => setPayNow(false)}>
              Peruuta
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Paper>
  );
}

const SuccessBody = ({ emailConfirmationSuccess }) => {
  if (emailConfirmationSuccess === false) {
    return (
      <Alert severity="warning" style={{ margin: "1em 0px" }}>
        <b>Varaus on onnistuneesti suoritettu</b>, mutta varaus vahvistusta ei
        voitu toimittaa sähköpostiin. Ota yhteys vuokraamoon vahvistaaksesi
        varauksen
      </Alert>
    );
  }
  return <P>Varaus on suoritettu ja saat pian vahvistuksen sähköpostiisi.</P>;
};

function Success({
  navigate,
  emailConfirmationSuccess,
  group,
  reservationId,
  setSnackbarMessage,
  theme,
  showReturnToCompanyUrl,
}) {
  const { handleActiveStep, handleClearCart, showBackToStartBtn } =
    useContext(AppContext);
  const [submitting, setSubmitting] = useState(false);

  // Reset cart when reservation has been completed, and customer is redirected to the beginning
  const reservationCompleted = () => {
    handleActiveStep(0);
    handleClearCart();
    navigate("/");
  };

  return (
    <Paper
      style={{
        maxWidth: 400,
        padding: `${theme.spacing(6)} ${theme.spacing(4)}`,
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(4),
        textAlign: "center",
      }}
    >
      <Typography variant="h4" component="h1">
        Kiitos varauksesta
      </Typography>

      <SuccessBody emailConfirmationSuccess={emailConfirmationSuccess} />
      {showBackToStartBtn && (
        <Button
          onClick={() => reservationCompleted()}
          color="primary"
          size="large"
          variant="contained"
          fullWidth
          disabled={submitting}
        >
          Palaa alkuun
        </Button>
      )}
      {showReturnToCompanyUrl && (
        <Button
          onClick={() => {
            if (group === "tampereensaunalautta") {
              window.location.replace("https://saunalauttaristeilyt.fi/");
            }
          }}
          color="primary"
          size="medium"
          variant="contained"
          disabled={submitting}
          fullWidth
        >
          Palaa vuokraamon verkkosivuille
        </Button>
      )}
    </Paper>
  );
}

export default function Confirmation() {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const container = useRef();
  const [emailConfirmationSuccess, setEmailConfirmationSuccess] =
    useState(false);
  const [failedOrderId, setFailedOrderId] = useState(false);
  const [reservationId, setReservationId] = useState(null);
  const [mustSign, setMustSign] = useState(false);
  const [paymentOnlineOnly, setPaymentOnlineOnly] = useState(false);
  const [group, setGroup] = useState();
  const [snackbarMessage, setSnackbarMessage] = useState();
  const {
    activeStep,
    companies,
    handleActiveStep,
    returnTime,
    startTime,
    selectedCompany,
  } = useContext(AppContext);

  const showReturnToCompanyUrl = useMemo(() => {
    if (!(group === "tampereensaunalautta")) return false;
    const enabledUrls = [process.env.REACT_APP_HOSTING_URL_CUSTOMER];
    if (enabledUrls.includes(window.location.origin)) return true;
    return false;
  }, [group]);

  useEffect(() => {
    async function gtmPurchaseEvent(reservationId) {
      // ecommerce gtm
      const response = await API.graphql(
        graphqlOperation(getReservationInfo, {
          reservationId: reservationId,
        })
      );
      const reservationInfo = response.data.getReservationInfo;
      gtmPurchase(reservationInfo);
    }

    container.current.scrollIntoView();
    const query = new URLSearchParams(location.search);
    if (query.get("group")) {
      setGroup(query.get("group"));
    } else if (location.state?.group) {
      setGroup(location.state?.group);
    }
    // If ORDER_NUMBER query param exists, payment not succesful
    if (query.get("ORDER_NUMBER") || location.state?.ORDER_NUMBER) {
      // Set failedOrderId to trigger PaymentFailure, but also reservationId.
      // It is possible to get to Success from PaymentFailure, so we need reservationId.
      const orderNumber =
        query.get("ORDER_NUMBER") || location.state?.ORDER_NUMBER;
      setFailedOrderId(orderNumber);
      setReservationId(orderNumber);
      const paymentOnlineOnly =
        query.get("paymentOnlineOnly") || location.state?.paymentOnlineOnly;
      setPaymentOnlineOnly(paymentOnlineOnly === "true");
    } else {
      // Reservation successful
      let gtm = false;
      if (location.state?.gtm || query.get("gtm") === "true") {
        gtm = true;
      }
      // Get emailSuccess from query param, if doesnt exist get from state
      const emailSuccess = query.get("EMAIL");
      if (!!emailSuccess) {
        setEmailConfirmationSuccess(emailSuccess === "true" ? true : false);
      } else {
        setEmailConfirmationSuccess(location.state?.emailConfirmationSuccess);
      }
      // Get reservation id from query param, if doesnt exist get from state
      let newReservationId = query.get("reservationId");
      if (!!newReservationId) {
        setReservationId(newReservationId);
      } else {
        setReservationId(location.state?.reservationId);
        newReservationId = location.state?.reservationId;
      }
      gtmPurchaseEvent(newReservationId);
    }
  }, []);

  return (
    <Container ref={container}>
      <Grid container direction="column" alignItems="center">
        {failedOrderId ? (
          <>
            <PaymentFailure
              reservationId={failedOrderId}
              group={group}
              setFailedOrderId={setFailedOrderId}
              navigate={navigate}
              setSnackbarMessage={setSnackbarMessage}
              setEmailConfirmationSuccess={setEmailConfirmationSuccess}
              paymentOnlineOnly={paymentOnlineOnly}
              theme={theme}
              showReturnToCompanyUrl={showReturnToCompanyUrl}
            />
          </>
        ) : (
          <Success
            navigate={navigate}
            emailConfirmationSuccess={emailConfirmationSuccess}
            group={group}
            reservationId={reservationId}
            setSnackbarMessage={setSnackbarMessage}
            theme={theme}
            showReturnToCompanyUrl={showReturnToCompanyUrl}
          />
        )}
      </Grid>
      <Snackbar
        open={!!snackbarMessage}
        onClose={() => setSnackbarMessage(null)}
        autoHideDuration={6000}
        message={snackbarMessage}
      />
    </Container>
  );
}
